"use client";

import React, { useState } from "react";
import { Bot, Cpu, CircleCheck as CheckCircle2, ArrowRight } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
interface EquipmentItem {
  id: string;
  name: string;
  price: string;
  unit: string;
  note?: string;
  tagline: string;
}
const purchaseItems: EquipmentItem[] = [{
  id: "whalesbot-purchase",
  name: "Whalesbot",
  price: "6.500.000",
  unit: "VNĐ / bộ",
  note: "(Tùy loại)",
  tagline: "Bộ giáo cụ chuyên dụng chuẩn quốc tế cho lứa tuổi mầm non và tiểu học"
}, {
  id: "ev3-purchase",
  name: "EV3",
  price: "15.000.000 – 18.000.000",
  unit: "VNĐ / bộ",
  note: "(Tùy độ mới)",
  tagline: "Nền tảng robotics nâng cao tiêu chuẩn cho học sinh trung học và thi đấu"
}];
const rentalItems: EquipmentItem[] = [{
  id: "whalesbot-rental",
  name: "Whalesbot",
  price: "600.000",
  unit: "VNĐ / bộ / tháng",
  note: "Giảm 10% nếu hợp đồng thuê 1 năm học (tối thiểu 9 tháng)",
  tagline: "Linh hoạt ngân sách, bảo dưỡng định kỳ và đổi mới linh kiện"
}, {
  id: "ev3-rental",
  name: "EV3",
  price: "1.500.000",
  unit: "VNĐ / bộ / tháng",
  tagline: "Đầy đủ cảm biến, động cơ và bảo hành hỗ trợ kỹ thuật suốt khóa"
}];
export default function EquipmentPricing() {
  const [activeTab, setActiveTab] = useState<string>("purchase");
  return <section id="equipmentpricing" data-nav="light" className="relative bg-background text-foreground py-20 lg:py-28 overflow-hidden">
      {/* Subtle decorative background texture */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--border)_1px,_transparent_1px)] [background-size:24px_24px] opacity-40" />

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-12 lg:mb-16">
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground">
            THIẾT BỊ ROBOTICS
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Lựa chọn mua hoặc thuê thiết bị phục vụ triển khai chương trình.
          </p>
        </div>

        {/* Tabs Control */}
        <Tabs defaultValue="purchase" value={activeTab} onValueChange={setActiveTab} className="w-full flex flex-col items-center">
          <TabsList className="bg-muted p-1.5 rounded-xl border border-border h-auto inline-flex mb-10 shadow-xs">
            <TabsTrigger value="purchase" className="px-6 py-2.5 rounded-lg font-semibold text-sm sm:text-base transition-all data-[state=active]:bg-accent data-[state=active]:text-accent-foreground data-[state=active]:shadow-md">
              MUA THIẾT BỊ
            </TabsTrigger>
            <TabsTrigger value="rental" className="px-6 py-2.5 rounded-lg font-semibold text-sm sm:text-base transition-all data-[state=active]:bg-accent data-[state=active]:text-accent-foreground data-[state=active]:shadow-md">
              THUÊ THIẾT BỊ
            </TabsTrigger>
          </TabsList>

          {/* Mua Thiết Bị Tab */}
          <TabsContent value="purchase" className="w-full outline-none">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              {purchaseItems.map((item, i) => <div key={item.id} data-index={i} className="flex">
                  <Card className="flex-1 flex flex-col justify-between border border-border bg-card shadow-sm hover:shadow-md transition-shadow rounded-2xl p-6 sm:p-8">
                    <div>
                      <CardHeader className="p-0 mb-6 flex-row items-center gap-3 space-y-0">
                        <div className="p-3 rounded-xl bg-primary/10 text-primary shrink-0">
                          {item.name === "Whalesbot" ? <Bot className="size-6 text-primary" /> : <Cpu className="size-6 text-primary" />}
                        </div>
                        <div>
                          <CardTitle className="text-2xl font-bold tracking-tight text-card-foreground">
                            {item.name}
                          </CardTitle>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {item.tagline}
                          </p>
                        </div>
                      </CardHeader>

                      <CardContent className="p-0 mb-6">
                        <div className="pt-4 border-t border-border">
                          <div className="flex flex-col">
                            <span className="text-3xl sm:text-4xl font-extrabold tracking-tight text-foreground">
                              {item.price}
                            </span>
                            <span className="text-sm font-medium text-muted-foreground mt-1">
                              {item.unit}
                            </span>
                          </div>

                          {item.note && <div className="mt-4 flex items-start gap-2 bg-muted/50 rounded-lg p-3 border border-border/60">
                              <CheckCircle2 className="size-4 text-secondary shrink-0 mt-0.5" />
                              <span className="text-xs text-foreground/85 font-medium leading-relaxed">
                                {item.note}
                              </span>
                            </div>}
                        </div>
                      </CardContent>
                    </div>

                    <CardFooter className="p-0 pt-4">
                      <Button asChild className="w-full font-medium" variant="default">
                        <Link to="/FreeTrialContactPage">
                          Tư vấn thiết bị
                          <ArrowRight className="size-4 ml-1.5" />
                        </Link>
                      </Button>
                    </CardFooter>
                  </Card>
                </div>)}
            </div>
          </TabsContent>

          {/* Thuê Thiết Bị Tab */}
          <TabsContent value="rental" className="w-full outline-none">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              {rentalItems.map((item, i) => <div key={item.id} data-index={i} className="flex">
                  <Card className="flex-1 flex flex-col justify-between border border-border bg-card shadow-sm hover:shadow-md transition-shadow rounded-2xl p-6 sm:p-8">
                    <div>
                      <CardHeader className="p-0 mb-6 flex-row items-center gap-3 space-y-0">
                        <div className="p-3 rounded-xl bg-primary/10 text-primary shrink-0">
                          {item.name === "Whalesbot" ? <Bot className="size-6 text-primary" /> : <Cpu className="size-6 text-primary" />}
                        </div>
                        <div>
                          <CardTitle className="text-2xl font-bold tracking-tight text-card-foreground">
                            {item.name}
                          </CardTitle>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {item.tagline}
                          </p>
                        </div>
                      </CardHeader>

                      <CardContent className="p-0 mb-6">
                        <div className="pt-4 border-t border-border">
                          <div className="flex flex-col">
                            <span className="text-3xl sm:text-4xl font-extrabold tracking-tight text-foreground">
                              {item.price}
                            </span>
                            <span className="text-sm font-medium text-muted-foreground mt-1">
                              {item.unit}
                            </span>
                          </div>

                          {item.note && <div className="mt-4 flex items-start gap-2 bg-muted/50 rounded-lg p-3 border border-border/60">
                              <CheckCircle2 className="size-4 text-secondary shrink-0 mt-0.5" />
                              <span className="text-xs text-foreground/85 font-medium leading-relaxed">
                                {item.note}
                              </span>
                            </div>}
                        </div>
                      </CardContent>
                    </div>

                    <CardFooter className="p-0 pt-4">
                      <Button asChild className="w-full font-medium" variant="default">
                        <Link to="/FreeTrialContactPage">
                          Đăng ký thuê
                          <ArrowRight className="size-4 ml-1.5" />
                        </Link>
                      </Button>
                    </CardFooter>
                  </Card>
                </div>)}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </section>;
}